import { GetServerSideProps } from 'next';
import { GraphQLError } from 'graphql';
import { ApolloQueryResult } from '@apollo/client';
import { getApolloClient } from '@/lib/ApolloClient';
import { LDTargetAttributes, FeatureFlags, PageTemplate, GAMTargetingRules } from '@/types/common';
import getQueryParams from '@/utils/getQueryParams';
import { SERVER_FEATURE_FLAGS, UI_FEATURE_FLAGS } from '@/constants';
import getDeviceCharacteristics from '@/utils/getDeviceCharacteristics';
import reqHeaderToEntriesObj from '@/utils/reqHeaderToEntriesObj';
import { seoSegmentationListingPage as seoSegmentationListingPageQuery } from '@/API/seoSegmentationListingPage';
import {
  seoSegmentationListingPage as seoSegmentationListingPageType,
  seoSegmentationListingPage_seoSegmentationDetails_SEOSegmentationDetailsSuccess as seoSegmentationDetailsType,
  seoSegmentationListingPage_seoSegmentationDetails_SEOSegmentationDetailsError as seoSegmentationDetailsErrorType,
  seoSegmentationListingPage_seoSegmentationSearchResults_SEOSegmentationSearchResultsSuccess as seoSegmentationSearchResultsSuccessType,
} from '@/__generated__/seoSegmentationListingPage';
import { SEOSegmentationSearchingFor } from '@/__generated__/globalTypes';
import { getGAMVerticalShortName } from '@/utils/GoogleAdsManager/getGAMVerticalShortName';
import { getGAMContentType } from '@/utils/GoogleAdsManager/getGAMContentType';

export interface SegmentationListingPageProps {
  seoSegmentId: string;
  initialPageNumber: number;
  ipAddress: string;
  botInfo: string;
  userGeoInfo: string;
  ldClientFlags: FeatureFlags;
  pageData: {
    seoSegmentationSearchResults: NonNullable<seoSegmentationSearchResultsSuccessType>;
    seoSegmentationDetails: NonNullable<seoSegmentationDetailsType['segmentationDetails']>;
    seoSegmentationHeroImage: seoSegmentationListingPageType['seoSegmentationHeroImage'];
    seoSegmentationBreadcrumbs: seoSegmentationListingPageType['seoSegmentationBreadcrumbs'];
    getSEOSegmentationIntro: Exclude<
      seoSegmentationListingPageType['getSEOSegmentationIntro'],
      undefined
    >;
    seoSegmentationStats: seoSegmentationListingPageType['seoSegmentationStats'] | null;
    getSEOSegmentationFAQs: seoSegmentationListingPageType['getSEOSegmentationFAQs'];
    getSEOSegmentationLinkSections: seoSegmentationListingPageType['getSEOSegmentationLinkSections'];
    seoArticlesList: seoSegmentationListingPageType['seoArticlesList'];
    seoNavigationLinkSection: seoSegmentationListingPageType['seoNavigationLinkSection'];
  };
  userAgent: string;
  deviceChar: {
    [key: string]: string;
  };
  testName: string;
  numberOfReviewsPerProfile: number;
  pageTemplate: PageTemplate;
  additionalGAMRules: Partial<GAMTargetingRules>;
  shuffleSeed: number;
}

const isLocal = process.env.NODE_ENV === 'development';

// eslint-disable-next-line complexity
const getListPageData: GetServerSideProps = async ({ query, req, res }) => {
  const startPageGSSP = performance.now();
  const apolloOptions = {
    logError: req.log.error.bind(req.log),
    logInfo: req.log.info.bind(req.log),
  };
  const startApolloClient = performance.now();
  const apolloClient = getApolloClient(apolloOptions);
  const stopApolloClient = performance.now();
  const shuffleSeed = Math.floor(Math.random() * 2147483647);
  const serverFeatureFlags = req.careContext?.ldServerFlags ?? false;
  const userAgent = req.headers['user-agent'] || '';
  const deviceCharHeader = req.headers['x-akamai-device-characteristics']?.toString() || '';
  const deviceChar = isLocal
    ? getDeviceCharacteristics(userAgent, deviceCharHeader)
    : reqHeaderToEntriesObj(deviceCharHeader, ';');

  const { seoSegmentId, pageNumber, local, location, testIDToFireTestExposureEvent } =
    getQueryParams(query);

  const isJobSegment = Boolean(query.segment?.toString().includes('jobs'));

  const botInfo = req.headers['Akamai-Bot']?.toString() || '';
  const userGeoInfo = req.headers['x-akamai-edgescape']?.toString() || '';
  const ipAddressArray = req.headers['x-forwarded-for'];
  let ipAddress;
  if (typeof ipAddressArray !== 'undefined') {
    [ipAddress] = ipAddressArray.toString().split(',');
  } else {
    ipAddress = '';
  }
  const numberOfReviewsPerProfile = 1;
  const startGetPageData = performance.now();
  if (
    serverFeatureFlags &&
    serverFeatureFlags[SERVER_FEATURE_FLAGS.SEO_MFE_LOG_AKAMAI_HEADER]?.value
  ) {
    req.log.info({
      event: 'x-akamai-edgescape header',
      seoSegmentId,
      userAgent,
      deviceChar,
      ipAddress,
      akamaiHeader: userGeoInfo,
    });
  }

  function getAllErrors(queryResult: ApolloQueryResult<seoSegmentationListingPageType>) {
    const errors: { [key: string]: GraphQLError | seoSegmentationDetailsErrorType['errors'] } = {};

    if (queryResult.data.seoSegmentationDetails.__typename === 'SEOSegmentationDetailsError') {
      errors.seoSegmentationDetailsError = queryResult.data.seoSegmentationDetails.errors;
    }

    if (
      queryResult.data.seoSegmentationSearchResults.__typename ===
      'SEOSegmentationSearchResultsError'
    ) {
      errors.seoSegmentationSearchResultsError =
        queryResult.data.seoSegmentationSearchResults.errors;
    }

    queryResult.errors?.forEach((error) => {
      if (error.path) {
        // path is the query name (e.g. getSEOSegmentationSearchResults) its represented as a singleton array
        errors[`${error.path[0]}Error`] = error as GraphQLError;
      }
    });

    return errors;
  }

  const seoSegmentationListingPage = await seoSegmentationListingPageQuery(apolloClient, {
    filters: null,
    pageNumber,
    seoSegmentId,
    ipAddress,
    botInfo,
    userGeoInfo,
    userAgent,
    numberOfReviewsPerProfile,
    local,
    locationId: location,
    isJobSegment,
  });
  const stopGetPageData = performance.now();

  // Server error (e.g. validation or syntax resulting in a 400)
  // https://www.apollographql.com/docs/react/data/error-handling/#graphql-errors
  if (seoSegmentationListingPage.error !== null) {
    req.log.error({
      event: 'SeoListingPageLoadError',
      seoSegmentId,
      userAgent,
      deviceChar,
      query,
      res,
      errors: seoSegmentationListingPage.error,
    });

    return {
      notFound: true,
    };
  }

  const seoSegmentationListingPageData = seoSegmentationListingPage.queryResponse.data;

  // Resolver Errors returned as "errors"
  if (seoSegmentationListingPage.queryResponse.errors) {
    const errors = getAllErrors(seoSegmentationListingPage.queryResponse);

    req.log.error({
      event: 'SeoListingPageLoadError',
      seoSegmentId,
      userAgent,
      deviceChar,
      query,
      res,
      errors,
    });
  }

  // Resolver errors returned as data
  // return 404 for resolvers containing essential data for UI rendering
  // https://www.apollographql.com/docs/react/data/error-handling/#graphql-errors
  if (
    seoSegmentationListingPageData.seoSegmentationDetails.__typename ===
      'SEOSegmentationDetailsError' ||
    seoSegmentationListingPageData.seoSegmentationSearchResults.__typename ===
      'SEOSegmentationSearchResultsError'
  ) {
    req.log.error({
      event: 'SeoListingPageLoadError',
      seoSegmentId,
      userAgent,
      deviceChar,
      query,
      res,
      errors: getAllErrors(seoSegmentationListingPage.queryResponse),
    });

    return {
      notFound: true,
    };
  }

  if (seoSegmentationListingPageData.seoSegmentationDetails.segmentationDetails === null) {
    req.log.error({
      event: 'SeoListingPageLoadError',
      seoSegmentId,
      userAgent,
      deviceChar,
      query,
      res,
      errorMsg: 'ERROR - seoSegmentationDetails is null',
    });

    return {
      notFound: true,
    };
  }

  const startLD = performance.now();
  const ldClientFlags: FeatureFlags = {};
  const { pageType, serviceIdForMember, serviceId, segmentationSubService, searchingFor } =
    seoSegmentationListingPageData.seoSegmentationDetails.segmentationDetails;

  const pageTemplate = (() => {
    if (
      searchingFor === SEOSegmentationSearchingFor.ALL_JOB ||
      searchingFor === SEOSegmentationSearchingFor.SEEKER_JOB ||
      searchingFor === SEOSegmentationSearchingFor.SMB_JOB
    ) {
      return PageTemplate.JOB_DIRECTORY;
    }

    return PageTemplate.CAREGIVER_DIRECTORY;
  })();

  const ldTargetAttributes: LDTargetAttributes = {
    pageType,
    serviceIdForMember,
    serviceId,
    segmentationSubService,
    seoSegmentId,
    deviceChar,
    searchingFor,
    pageTemplate,
  };

  const { ldClient, ldUser } = req.careContext || {};
  if (ldClient && ldUser) {
    ldUser.custom = {
      ...ldUser.custom,
      serviceIdForMember,
      seoSegmentId,
      pageType,
      subService: segmentationSubService || '',
      serviceId,
      searchingFor,
      pageTemplate,
    };

    const clientFlagPromises = Object.values(UI_FEATURE_FLAGS)
      .filter((flag) => flag.condition(ldTargetAttributes))
      .map(async (flag) => {
        ldClientFlags[flag.id] = await ldClient.variationDetail(flag.id, ldUser, undefined);
      });
    await Promise.all(clientFlagPromises);
  }
  const stopLD = performance.now();

  const additionalGAMRules: Partial<GAMTargetingRules> = {
    vertical: getGAMVerticalShortName(serviceId),
    contType: getGAMContentType({ searchingFor }),
  };

  const pageProps: { props: SegmentationListingPageProps } = {
    props: {
      seoSegmentId,
      initialPageNumber: pageNumber,
      ipAddress,
      botInfo,
      userGeoInfo,
      ldClientFlags,
      pageData: {
        seoSegmentationSearchResults: seoSegmentationListingPageData.seoSegmentationSearchResults,
        seoSegmentationDetails:
          seoSegmentationListingPageData.seoSegmentationDetails.segmentationDetails,
        seoSegmentationHeroImage: seoSegmentationListingPageData.seoSegmentationHeroImage,
        seoSegmentationBreadcrumbs: seoSegmentationListingPageData.seoSegmentationBreadcrumbs,
        getSEOSegmentationIntro: seoSegmentationListingPageData.getSEOSegmentationIntro ?? null,
        seoSegmentationStats: seoSegmentationListingPageData.seoSegmentationStats ?? null, // not requested on jobs
        getSEOSegmentationFAQs: seoSegmentationListingPageData.getSEOSegmentationFAQs,
        getSEOSegmentationLinkSections:
          seoSegmentationListingPageData.getSEOSegmentationLinkSections,
        seoArticlesList: seoSegmentationListingPageData.seoArticlesList,
        seoNavigationLinkSection: seoSegmentationListingPageData.seoNavigationLinkSection ?? null,
      },
      userAgent,
      deviceChar,
      testName: testIDToFireTestExposureEvent,
      numberOfReviewsPerProfile,
      pageTemplate,
      additionalGAMRules,
      shuffleSeed,
    },
  };

  req.log.info({
    event: 'SeoListingPageLoadSuccess',
    seoSegmentId,
    userAgent,
    deviceChar,
    query,
    res,
  });
  const stopPageGSSP = performance.now();
  req.careContext.serverTimings.pageGSSP = stopPageGSSP - startPageGSSP;
  req.careContext.serverTimings.ldClientFlags = stopLD - startLD;
  req.careContext.serverTimings.apolloClient = stopApolloClient - startApolloClient;
  req.careContext.serverTimings.pageData = stopGetPageData - startGetPageData;
  return pageProps;
};
export default getListPageData;
